import type { Session } from '@ory/client-fetch';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getSession } from '~/lib/ory/getSession';
import { resolveAuthMethods } from '~/lib/auth/resolveAuthMethods';
import { BaseUrl } from '~/types/baseUrl';
import { getBaseUrl } from '~/utils/getBaseUrl';
import { isAllowedEmailDomain } from '~/utils/isAllowedEmailDomain';
import { withOrgSlug } from '~/utils/orgSlug';
import { logout } from '../utilities/logout';

export const SessionContext = createContext<{
  session: Session | null | undefined;
  isVersori: boolean;
  isSsoSetupRequired: boolean;
  ssoProvider: string | null;
  checkSession: ({
    unsavedFiles,
    projectId,
    projectName,
    path,
  }: {
    unsavedFiles?: Set<string>;
    projectId?: string;
    projectName?: string;
    path?: string;
  }) => Promise<boolean>;
  handleAuthAfterInitialPrompt: ({ initialPrompt, path }: { initialPrompt: string | null; path?: string }) => void;
  handleAuthBackToIntegration: ({
    projectId,
    projectName,
    path,
  }: {
    projectId?: string;
    projectName?: string;
    path?: string;
  }) => void;
  handleLogout: () => void;
  handleLogin: () => void;
  handleLogoutAndLogin: (loginParams?: Record<string, string>) => void;
  handleSignup: () => void;
  getLatestSession: () => Promise<Session | false>;
}>({
  session: undefined,
  isVersori: false,
  isSsoSetupRequired: false,
  ssoProvider: null,
  checkSession: async () => false,
  handleLogout: () => { },
  handleAuthAfterInitialPrompt: () => { },
  handleAuthBackToIntegration: () => { },
  handleLogin: () => { },
  handleLogoutAndLogin: () => { },
  handleSignup: () => { },
  getLatestSession: async () => false,
});

export type SessionContext = typeof SessionContext;

export type SessionProviderProps = {
  children: ReactNode;
};

export const authBaseUrl: BaseUrl = {
  production: 'https://auth.versori.com',
  staging: 'https://auth-staging.versori.com',
  development: import.meta.env.VITE_AUTH_URL || 'http://localhost:3030',
};

export const kratosBaseUrl: BaseUrl = {
  production: 'https://auth.versori.com/.idp',
  staging: 'https://auth-staging.versori.com/.idp',
  development: import.meta.env.VITE_KRATOS_URL || 'http://localhost:4433',
};

export const appBaseUrl: BaseUrl = {
  production: 'https://ai.versori.com',
  staging: 'https://ai-staging.versori.com',
  development: import.meta.env.VITE_LOCAL_TARGET || 'http://localhost:5173',
};

const ACCOUNT_SECURITY_PATH = '/account';
const ACCOUNT_SECURITY_CONTENT = 'security';

export function accountSecurityUrl(baseUrl = getBaseUrl(appBaseUrl), orgSlug?: string | null): string {
  return withOrgSlug(`${baseUrl}${ACCOUNT_SECURITY_PATH}?content=${ACCOUNT_SECURITY_CONTENT}`, orgSlug);
}

export function emailVerificationUrl(returnTo: string, baseUrl = getBaseUrl(authBaseUrl)): string {
  const url = new URL('/verify', baseUrl);
  url.searchParams.set('intent', 'verify');
  url.searchParams.set('return_to', returnTo);
  return url.toString();
}

/** True when the user is already on the in-app Security tab or returning to its Ory settings flow. */
export function isAlreadyOnAccountSecurity(location: Pick<Location, 'pathname' | 'search'>): boolean {
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  if (pathname !== ACCOUNT_SECURITY_PATH) {
    return false;
  }
  const searchParams = new URLSearchParams(location.search);
  return (
    searchParams.get('content') === ACCOUNT_SECURITY_CONTENT ||
    (!searchParams.has('content') && searchParams.has('flow'))
  );
}

type SsoState = {
  isSsoSetupRequired: boolean;
  ssoProvider: string | null;
};

function usedOidc(session: Session): boolean {
  return (session.authentication_methods ?? []).some((method) => method.method === 'oidc');
}

export async function resolveSsoState(session: Session): Promise<SsoState> {
  const email = session.identity?.traits?.email as string | undefined;
  if (!email) {
    return { isSsoSetupRequired: false, ssoProvider: null };
  }

  const authMethods = await resolveAuthMethods(email);
  const ssoProvider = authMethods?.ssoProvider ?? null;

  return {
    isSsoSetupRequired: Boolean(authMethods?.ssoEnforced && ssoProvider && !usedOidc(session)),
    ssoProvider,
  };
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [ssoState, setSsoState] = useState<SsoState>({
    isSsoSetupRequired: false,
    ssoProvider: null,
  });
  const { isSsoSetupRequired, ssoProvider } = ssoState;
  const isVersori = useMemo(() => {
    return isAllowedEmailDomain(session?.identity?.traits?.email);
  }, [session]);

  const handleAuthBackToIntegration = useCallback(
    ({ projectId, projectName, path = 'register' }: { projectId?: string; projectName?: string; path?: string }) => {
      if (session) {
        let returnTo = '';
        if (projectId) {
          returnTo = encodeURIComponent(
            `${getBaseUrl(appBaseUrl)}/integrations/${projectId}?projectName=${projectName}`
          );
        } else {
          returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
        }
        window.location.href = `${getBaseUrl(authBaseUrl)}/${path}?return_to=${returnTo}`;
      }
    },
    [session]
  );

  const handleAuthAfterInitialPrompt = useCallback(
    ({ initialPrompt, path = 'register' }: { initialPrompt: string | null; path?: string }) => {
      if (!initialPrompt) {
        return;
      }
      if (!session) {
        let id: string;
        try {
          id = JSON.parse(initialPrompt).id;
        } catch (error) {
          console.error('Failed to parse initial prompt:', error);
          return;
        }
        const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}/integrations/${id}`);
        window.location.href = `${getBaseUrl(authBaseUrl)}/${path}?return_to=${returnTo}`;
      }
    },
    [session]
  );

  const handleLogin = useCallback(() => {
    const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
    window.location.href = `${getBaseUrl(authBaseUrl)}/signin?return_to=${returnTo}`;
  }, []);

  const handleSignup = useCallback(() => {
    const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
    window.location.href = `${getBaseUrl(authBaseUrl)}/register?return_to=${returnTo}`;
  }, []);

  useEffect(() => {
    let cancelled = false;

    getSession().then((data) => {
      if (cancelled) {
        return;
      }

      // Unblock first paint. The lookup below redirects if SSO is still required.
      setSession(data);

      if (!data) {
        return;
      }

      void resolveSsoState(data).then((nextSsoState) => {
        if (cancelled) {
          return;
        }

        setSsoState(nextSsoState);

        if (nextSsoState.isSsoSetupRequired && !isAlreadyOnAccountSecurity(window.location)) {
          const orgSlug = new URLSearchParams(window.location.search).get('org');
          window.location.href = accountSecurityUrl(undefined, orgSlug);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const checkSession = useCallback(async () => {
    const data = await getSession();
    if (!data) {
      return false;
    }
    return true;
  }, []);

  const getLatestSession = useCallback(async () => {
    const data = await getSession();
    if (!data) {
      return false;
    }
    setSession(data);
    setSsoState(await resolveSsoState(data));
    return data;
  }, []);

  const handleLogout = useCallback(() => {
    logout(getBaseUrl(kratosBaseUrl), getBaseUrl(appBaseUrl));
  }, []);

  const handleLogoutAndLogin = useCallback((loginParams?: Record<string, string>) => {
    const orgSlug = new URLSearchParams(window.location.search).get('org');
    const params = new URLSearchParams();
    params.set('return_to', withOrgSlug(getBaseUrl(appBaseUrl), orgSlug));
    if (loginParams) {
      for (const [key, value] of Object.entries(loginParams)) {
        params.set(key, key === 'return_to' ? withOrgSlug(value, orgSlug) : value);
      }
    }
    const returnTo = `${getBaseUrl(authBaseUrl)}/signin?${params.toString()}`;
    logout(getBaseUrl(kratosBaseUrl), returnTo);
  }, []);

  const value = useMemo(() => {
    return {
      session,
      isVersori,
      isSsoSetupRequired,
      ssoProvider,
      checkSession,
      getLatestSession,
      handleLogout,
      handleAuthAfterInitialPrompt,
      handleAuthBackToIntegration,
      handleLogin,
      handleLogoutAndLogin,
      handleSignup,
    };
  }, [
    session,
    isVersori,
    isSsoSetupRequired,
    ssoProvider,
    checkSession,
    getLatestSession,
    handleLogout,
    handleAuthAfterInitialPrompt,
    handleAuthBackToIntegration,
    handleLogin,
    handleLogoutAndLogin,
    handleSignup,
  ]);

  if (session === undefined) {
    return null;
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const session = useContext(SessionContext);

  return session;
}
